import { NgClass } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	signal,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';

import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [NgClass, TranslatePipe],
	selector: 'vp-read-more',
	templateUrl: './read-more.html',
})
export class ReadMoreComponent {
	private readonly viewPortService = inject(ViewPortService);

	expanded = signal(false);
	screenResized = signal(0);

	// only when clamped is known for sure to be false and not expanded, hide toggle
	// clamped can only be determined when not expanded
	showToggle = computed(() => {
		if (this.expanded()) {
			return true;
		}
		// depend on screenResized just to be notified that we might need to check the overflow again
		this.screenResized();
		// check overflow after being collapsed even without a resize
		return this.overflow() > 0;
	});

	@ViewChild('content') content!: ElementRef<HTMLDivElement>;

	constructor() {
		this.viewPortService.screenWidth$
			.pipe(takeUntilDestroyed(), debounceTime(300))
			.subscribe(() => this.updateScreenResized());
	}

	updateScreenResized() {
		this.screenResized.set(this.overflow());
	}

	overflow() {
		const el = this.content?.nativeElement;
		if (el && !this.expanded()) {
			return el.scrollHeight - el.clientHeight;
		}
		return -1;
	}

	toggle() {
		this.expanded.set(!this.expanded());
	}
}
